import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useWallet } from '../../contexts/WalletContext';
import { validateStoredJWT, clearJWT } from '../../utils/jwtUtils';

/**
 * Protected Route Component
 * Validates JWT before allowing access to protected pages
 * Redirects to home if JWT is expired or missing
 * Waits for JWT authentication to complete if wallet is connected
 */
const ProtectedRoute = ({ children }) => {
  const { connected, walletAddress, authenticating, jwtToken } = useWallet();
  const location = useLocation();
  const [waitingForAuth, setWaitingForAuth] = useState(false);

  useEffect(() => {
    // Check JWT validity on mount and when location changes
    const jwtValidation = validateStoredJWT();
    
    if (jwtValidation.expired) {
      console.log('🔑 JWT expired, clearing and redirecting to home');
      clearJWT();
      setWaitingForAuth(false);
    } else if (!jwtValidation.valid && !jwtValidation.token && connected && !authenticating) {
      console.log('🔑 No JWT found but wallet connected, waiting for authentication...');
      setWaitingForAuth(true);
    } else if (jwtValidation.valid) {
      setWaitingForAuth(false);
    }
  }, [location.pathname, connected, authenticating, jwtToken]);

  // Check wallet connection
  if (!connected || !walletAddress) {
    console.log('⛔ Wallet not connected, redirecting to home');
    return <Navigate to="/" replace />;
  }

  // If authentication is in progress, show loading
  if (authenticating || waitingForAuth) {
    console.log('⏳ Authentication in progress, showing loading...');
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        gap: '24px',
        backgroundColor: '#000000'
      }}>
        <img 
          src="https://images.solanadeads.com/graveforge.png" 
          alt="GraveForge Logo"
          style={{ 
            width: '200px',
            height: 'auto',
            animation: 'pulse 2s ease-in-out infinite'
          }}
        />
        <CircularProgress sx={{ color: '#7d1f1f' }} />
        <div style={{ 
          fontSize: '18px',
          color: '#a3a3a3',
          fontWeight: 500
        }}>
          Authenticating wallet...
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </Box>
    );
  }

  // Validate JWT
  const jwtValidation = validateStoredJWT();
  
  if (!jwtValidation.valid) {
    if (jwtValidation.expired) {
      console.log('⛔ JWT expired, redirecting to home for re-authentication');
      clearJWT();
    } else {
      console.log('⛔ No valid JWT after authentication attempt, redirecting to home');
    }
    return <Navigate to="/" replace />;
  }

  // All checks passed, render the protected content
  return children;
};

export default ProtectedRoute;
